import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import moment from 'moment';
import { AnimatePresence, motion, Variants } from 'motion/react';
import {
    alpha,
    Box,
    Card,
    Divider,
    LinearProgress,
    Skeleton,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import useLocales from '../../hooks/useLocales';
import useReachlinkPlusSubscription from '../../hooks/useReachlinkPlusSubscription';
import Iconify from '../Iconify';
import { GET_USAGE_SUMMARY } from '../../graphql/resolvers/queries';
import { SubscriptionFeature, Usage, UsageSummary } from '../../@types/shared.types';
import { fPrice } from '../../utils/formatNumber';
import { fadeVariants } from '../../utils/animations';

const USAGE_FEATURES_META: Record<
    SubscriptionFeature,
    { icon: string; labelKey: string; descriptorKey: string }
> = {
    JOURNALS: {
        icon: 'material-symbols:book-2-outline',
        labelKey: 'components.reachlinkPlusSubscriptionCard.usages.journals',
        descriptorKey: 'components.reachlinkPlusSubscriptionCard.usages.journalsDescriptor',
    },
    CAREBOT: {
        icon: 'material-symbols:robot-2-outline',
        labelKey: 'components.reachlinkPlusSubscriptionCard.usages.carebot',
        descriptorKey: 'components.reachlinkPlusSubscriptionCard.usages.carebotDescriptor',
    },
};

const USAGE_FEATURES_ORDER: SubscriptionFeature[] = ['JOURNALS', 'CAREBOT'];

const USAGES_SECTION_VARIANTS: Variants = {
    hidden: {
        opacity: 0,
        height: 0,
        transition: {
            opacity: { duration: 0.2, ease: 'easeIn' },
            height: { duration: 0.4, ease: 'easeInOut', delay: 0.2 },
        },
    },
    visible: {
        opacity: 1,
        height: 'auto',
        transition: {
            height: { duration: 0.4, ease: 'easeInOut' },
            opacity: { duration: 0.2, ease: 'easeOut', delay: 0.4 },
        },
    },
};

const getProgressColor = (usage: Usage): 'success' | 'warning' | 'error' => {
    if (!usage.canUse) return 'error';
    if (usage.shouldWarn) return 'warning';
    return 'success';
};

const ReachlinkPlusSubscriptionCard = () => {
    const theme = useTheme();
    const { translate } = useLocales();
    const [showUsages, setShowUsages] = useState(false);

    const { subscription, isSubscribed, loading, priceInCents } = useReachlinkPlusSubscription();
    const isFreePlan = !isSubscribed;

    const { data: usageData } = useQuery<{ usageSummary: UsageSummary }>(GET_USAGE_SUMMARY, {
        skip: !isFreePlan || loading,
    });

    const usagesByFeature = (usageData?.usageSummary?.usages ?? []).reduce<
        Partial<Record<SubscriptionFeature, Usage>>
    >((map, usage) => ({ ...map, [usage.feature]: usage }), {});

    const cardSx = {
        backgroundColor: theme.palette.tan.lighter,
        boxShadow: 'none',
        p: 2,
    };

    const subscribedCardSx = {
        background: `linear-gradient(101deg, ${alpha(theme.palette.info.lighter, 0.88)} 0%, ${alpha(
            theme.palette.primary.lightest ?? theme.palette.primary.lighter,
            0.9
        )} 100%)`,
        boxShadow: 'none',
        p: 2,
    };

    const detailsLinkSx = {
        cursor: 'pointer',
        color:
            theme.palette.mode === 'dark'
                ? theme.palette.primary.lighter
                : theme.palette.primary.main,
    };

    return (
        <AnimatePresence mode="wait" initial={false}>
            {loading ? (
                <Box
                    component={motion.div}
                    key="loading"
                    variants={fadeVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                >
                    <Card sx={cardSx}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Skeleton variant="circular" width={18} height={18} />
                            <Skeleton variant="text" width={80} sx={{ fontSize: '0.875rem' }} />
                        </Stack>
                        <Skeleton variant="text" width="100%" sx={{ fontSize: '0.75rem', mt: 1 }} />
                    </Card>
                </Box>
            ) : isFreePlan ? (
                <Box
                    component={motion.div}
                    key="not-subscribed"
                    variants={fadeVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                >
                    <Card sx={cardSx}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Box
                                component="img"
                                src="/assets/illustrations/sparkles.svg"
                                alt=""
                                aria-hidden="true"
                                sx={{ width: 18, height: 18 }}
                            />
                            <Typography variant="subtitle2">
                                {translate('components.reachlinkPlusSubscriptionCard.freePlan')}
                            </Typography>
                        </Stack>

                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={2}
                            sx={{ mt: 1 }}
                        >
                            <Typography variant="caption" color="text.secondary">
                                {translate(
                                    'components.reachlinkPlusSubscriptionCard.upgradeMessage'
                                )}{' '}
                                {!showUsages && (
                                    <Typography
                                        variant="caption"
                                        component="span"
                                        sx={detailsLinkSx}
                                        onClick={() => setShowUsages(true)}
                                    >
                                        {translate(
                                            'components.reachlinkPlusSubscriptionCard.moreDetails'
                                        )}
                                    </Typography>
                                )}
                            </Typography>
                            {/* <Button
                                variant="contained"
                                size="small"
                                onClick={() => {}}
                                sx={{ flexShrink: 0 }}
                            >
                                {translate('components.reachlinkPlusSubscriptionCard.upgradeButton')}
                            </Button> */}
                        </Stack>

                        <AnimatePresence initial={false}>
                            {showUsages && (
                                <Stack
                                    component={motion.div}
                                    key="usages-section"
                                    variants={USAGES_SECTION_VARIANTS}
                                    initial="hidden"
                                    animate="visible"
                                    exit="hidden"
                                    sx={{ gap: 2, overflow: 'hidden' }}
                                >
                                    <Divider sx={{ my: 1.5 }} />
                                    <Stack gap={1.5}>
                                        {USAGE_FEATURES_ORDER.map((feature) => {
                                            const usage = usagesByFeature[feature];
                                            if (!usage) return null;

                                            const meta = USAGE_FEATURES_META[feature];
                                            const remaining =
                                                usage.limit != null
                                                    ? Math.max(usage.limit - usage.usage, 0)
                                                    : 0;
                                            const value =
                                                usage.isUnlimited || !usage.limit
                                                    ? 0
                                                    : Math.min(
                                                          100,
                                                          (usage.usage / usage.limit) * 100
                                                      );

                                            return (
                                                <Stack key={feature} gap={0.5}>
                                                    <Stack
                                                        direction="row"
                                                        alignItems="center"
                                                        gap={1}
                                                    >
                                                        <Iconify
                                                            icon={meta.icon}
                                                            width={12}
                                                            height={12}
                                                        />
                                                        <Typography variant="caption">
                                                            {translate(meta.labelKey)}
                                                        </Typography>
                                                    </Stack>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={value}
                                                        color={getProgressColor(usage)}
                                                        sx={{ borderRadius: 1, height: 6 }}
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {translate(meta.descriptorKey, {
                                                            count: remaining,
                                                        })}
                                                    </Typography>
                                                </Stack>
                                            );
                                        })}
                                    </Stack>
                                    <Typography
                                        variant="caption"
                                        component="span"
                                        sx={detailsLinkSx}
                                        onClick={() => setShowUsages(false)}
                                    >
                                        {translate(
                                            'components.reachlinkPlusSubscriptionCard.hideDetails'
                                        )}
                                    </Typography>
                                </Stack>
                            )}
                        </AnimatePresence>
                    </Card>
                </Box>
            ) : (
                <Box
                    component={motion.div}
                    key="subscribed"
                    variants={fadeVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                >
                    <Card sx={subscribedCardSx}>
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Box
                                component="img"
                                src="/assets/illustrations/rl-plus.svg"
                                alt=""
                                aria-hidden="true"
                                sx={{ width: 18, height: 18 }}
                            />
                            <Typography variant="subtitle2" color={theme.palette.grey[900]}>
                                {translate(
                                    'components.reachlinkPlusSubscriptionCard.reachlinkPlus'
                                )}
                            </Typography>
                        </Stack>

                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                            {translate('components.reachlinkPlusSubscriptionCard.nextPayment', {
                                amount: fPrice(priceInCents ?? 0),
                                date: moment(subscription?.currentPeriodEndsAt).format(
                                    'D MMM YYYY'
                                ),
                            })}
                        </Typography>
                    </Card>
                </Box>
            )}
        </AnimatePresence>
    );
};

export default ReachlinkPlusSubscriptionCard;
